import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, MoreHorizontal, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import InstallPWAButton from "./InstallPWAButton";
import MusicToggle from "./MusicToggle";
import BottomNav from "./BottomNav";
import QuickSearch from "./QuickSearch";
import { useCompany } from "../context/CompanyContext";

/**
 * Role-specific "home" route the back button considers neutral. Pressing
 * back ON the home dashboard does nothing (the button is hidden there).
 */
const ROLE_HOMES = {
  admin: "/admin",
  client: "/cliente",
  employee: "/funcionario",
};

/**
 * Shell — universal hamburger-driven layout used in every authenticated area.
 *
 * The same structure renders on mobile and desktop:
 *  ┌──────────────────────────────────────────────┐
 *  │  ☰   [logo]                  🎵  ⬇  🔔     │  ← top header (sticky)
 *  ├──────────────────────────────────────────────┤
 *  │                                              │
 *  │   page content                               │
 *  │                                              │
 *  └──────────────────────────────────────────────┘
 *
 * Clicking the hamburger opens a left-side drawer with the role-specific nav
 * (Admin / Cliente / Funcionário — the `nav` prop is set per app).
 * The drawer closes on navigation, on ESC, on backdrop click and on X.
 * BottomNav (optional) is still visible on mobile for Cliente and Funcionário.
 */
export default function Shell({ children, title, nav, testIdPrefix, bottomNavItems = null }) {
  const { company, logoDark } = useCompany();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const go = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer on every navigation
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  // Prevent body scroll while the drawer is open
  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // ESC closes drawer for keyboard users
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = e => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  async function onLogout() {
    window.dispatchEvent(new Event("luxstay:stop-music"));
    setDrawerOpen(false);
    await logout();
    go("/");
  }

  // The back button is hidden on the role's home dashboard (nothing
  // meaningful to go back to). On every other authenticated page we show
  // it. Falls back to the role home when the history stack is empty
  // (e.g. user opened a deep link from email/push).
  const home = ROLE_HOMES[user?.role] || "/";
  const isHome = loc.pathname === home || loc.pathname === `${home}/`;
  function goBack() {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      go(home);
    }
  }

  const NavLinks = ({ onClick }) => (
    <nav className="flex flex-col gap-1 text-sm" aria-label={`Navegação ${title}`}>
      {nav.map(n => {
        const active = loc.pathname === n.path || loc.pathname.startsWith(n.path + "/");
        const badge = n.badge;
        return (
          <Link key={n.path} to={n.path} onClick={onClick}
                className={`px-3 py-2.5 rounded-sm transition-colors border-l-2 flex items-center justify-between gap-2 ${active ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]" : "border-transparent text-[#a0b2af] hover:text-[#fdfbf7] hover:bg-[#062c27]/50"}`}
                data-testid={`${testIdPrefix}-nav-${n.key}`}>
            <span className="truncate">{n.label}</span>
            {badge && Number(badge) > 0 && (
              <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-semibold bg-[#ef4444] text-white rounded-full animate-pulse-slow"
                    data-testid={`${testIdPrefix}-nav-${n.key}-badge`}>
                {Number(badge) > 99 ? "99+" : badge}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );

  const roleLabel = {
    admin: "Administrador",
    employee: "Funcionário",
    client: "Cliente / Host",
  }[user?.role] || "";

  return (
    <div className="min-h-screen bg-[#041f1b] text-[#fdfbf7]">
      {/* UNIVERSAL TOP HEADER (hamburger + logo + utilities) */}
      <header className="sticky top-0 z-30 bg-[#041f1b]/95 backdrop-blur border-b border-[#1a4640] px-3 md:px-6 py-3 flex items-center gap-2 md:gap-3">
        <button onClick={() => setDrawerOpen(true)}
                className="p-2 border border-[#1a4640] rounded-sm hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                aria-label="Abrir menu"
                aria-expanded={drawerOpen}
                data-testid="shell-menu-toggle">
          <Menu size={18} />
        </button>
        {!isHome && (
          <button onClick={goBack}
                  className="p-2 border border-[#1a4640] rounded-sm text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                  aria-label="Voltar atrás"
                  title="Voltar atrás"
                  data-testid="shell-back-btn">
            <ArrowLeft size={18} />
          </button>
        )}
        <Link to="/" className="flex-1 min-w-0 flex items-center gap-3">
          <span className="bg-[#fdfbf7] rounded-sm px-2.5 py-1 inline-flex items-center">
            <img src={logoDark} alt={company.trade_name} className="h-10 md:h-11 w-auto" />
          </span>
          <span className="hidden md:inline eyebrow text-[#a0b2af]">{title}</span>
        </Link>
        <div className="flex items-center gap-2">
          <QuickSearch />
          {/* Music toggle is omitted for clients — the floating Rádio
              LuxStay player (with PT-PT female voice ads) handles ambient
              music there. Admin + employees keep the simple toggle. */}
          {user?.role !== "client" && <MusicToggle />}
          <InstallPWAButton variant="inline" />
          <NotificationBell />
          <button onClick={onLogout}
                  className="p-2 border border-[#1a4640] rounded-sm text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                  aria-label="Terminar sessão"
                  title="Terminar sessão"
                  data-testid="header-logout-btn">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* UNIVERSAL LEFT DRAWER with role-specific nav */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex" data-testid="shell-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"
               onClick={() => setDrawerOpen(false)} />
          <aside className="relative w-[86%] max-w-sm bg-[#041f1b] border-r border-[#1a4640] p-6 flex flex-col overflow-y-auto animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between mb-8">
              <Link to="/" onClick={() => setDrawerOpen(false)}>
                <span className="bg-[#fdfbf7] rounded-sm px-3 py-2 inline-flex items-center">
                  <img src={logoDark} alt={company.trade_name} className="h-16 w-auto" />
                </span>
              </Link>
              <button onClick={() => setDrawerOpen(false)}
                      className="p-2 text-[#a0b2af] hover:text-[#d4af37] transition-colors"
                      aria-label="Fechar menu"
                      data-testid="shell-drawer-close">
                <X size={20} />
              </button>
            </div>
            <div className="mb-6">
              <div className="eyebrow mb-1">{title}</div>
              {roleLabel && <div className="text-[11px] text-[#d4af37] uppercase tracking-widest" data-testid="shell-drawer-role">{roleLabel}</div>}
            </div>
            <NavLinks onClick={() => setDrawerOpen(false)} />
            <div className="mt-auto pt-8 border-t border-[#1a4640]">
              <div className="text-xs text-[#a0b2af] mb-1">Sessão</div>
              <div className="text-sm mb-4 truncate" data-testid="session-user">{user?.name}</div>
              <button onClick={onLogout}
                      className="text-sm text-[#a0b2af] hover:text-[#d4af37] inline-flex items-center gap-2"
                      data-testid="logout-btn">
                <LogOut size={14} /> Terminar sessão
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="p-4 md:p-10 pb-24 lg:pb-12 max-w-7xl mx-auto">{children}</main>
      {bottomNavItems && (
        <BottomNav items={bottomNavItems.map(it =>
          it.key === "more"
            ? { ...it, icon: it.icon || MoreHorizontal, onClick: () => setDrawerOpen(true) }
            : it
        )} />
      )}
      <InstallPWAButton variant="banner" />
    </div>
  );
}
